// 简体中文(非官方翻译・機械翻訳。ネイティブ確認推奨)。歌名等专有名词保持原文。
import type { SiteDict } from "./ja";
import type { RelativeDate } from "../../../utils/postMeta";

const zhHans: SiteDict = {
  site: {
    titleSuffix: "!Legit - Reol 非官方粉丝网站",
    topTitle: "!Legit｜收录 Reol 全部歌曲、演唱会、歌单的非官方粉丝网站",
    defaultDescription: (years: number) =>
      `Reol(れをる)的非官方粉丝网站。收录全部歌曲的现场演出统计、历年演唱会歌单、MV 取景地(圣地)地图、粉丝类型测试等，可横向搜索 ${years} 年来的活动记录。`,
    breadcrumbHome: "首页",
  },
  header: {
    search: "搜索",
    searchTitle: "横向搜索歌曲、演唱会、取景地",
    toDark: "切换为深色模式",
    toLight: "切换为浅色模式",
    myPage: "我的页面",
    menu: "菜单",
    language: "语言",
  },
  footer: {
    officialSite: "官方网站",
    goods: "周边",
    disclaimer: "本网站为非官方粉丝网站，与 Reol 官方无任何关系。歌曲与视频仅使用官方嵌入及链接。",
    operator: "运营者(非官方粉丝网站)",
    privacy: "隐私政策(日文)",
    mtNotice: "本页由日文机器翻译而成。如发现翻译错误或不自然的表达，欢迎告诉我们：",
    mtContact: "X @twilightplc",
  },
  video: {
    expand: "将视频恢复为全宽显示",
    close: "关闭视频",
    unmute: "取消静音",
    mute: "静音",
    play: "播放视频",
  },
  home: {
    heroLine1Before: "了解 Reol 的",
    heroLine1Accent: "过去",
    heroLine1After: "，",
    heroLine2Indent: "了解 Reol 的",
    heroLine2Accent: "未来",
    heroLine2After: "。",
    heroEmMobile: 8.5,
    heroEmWide: 8.6,
    heroLead1: "歌曲、演唱会、歌单、取景地。从「れをる」时期至今，",
    heroLead2: "同时也是引导你前往官方内容的非官方资料库。",
    ticketCta: "前往官方网站购票 →",
    welcomeTitle: "初识 Reol",
    welcomeDesc: "代表曲与各时期导览，从哪个时期入坑都不会迷路。",
    liveTitle: "去看演唱会",
    liveDesc: (n: number) => `${n} 场演出的歌单资料库与首次参加指南。`,
    liveGuideLink: "阅读首次参加指南 →",
    dataTitle: "深入挖掘数据",
    dataDesc: (n: number) => `收录全部 ${n} 首歌曲的演出次数、首次演出、最近演出。`,
    timemachineTitle: "时光机",
    timemachineDesc: "用年月滑块重现当时的歌曲数、演出场数、演出次数。",
    cgraphTitle: "创作者关系图",
    cgraphDesc: "将参与歌曲制作的创作者之间的关系可视化。",
    heatmapTitle: "演出地点地图",
    heatmapDesc: "按都道府县以颜色区分演出场数的热力图。",
    sorterTitle: "歌曲排序器",
    sorterDesc: "每次二选一的淘汰赛，决定你最喜欢的一首歌。",
    grammarTitle: "歌单语法",
    grammarDesc: "用数据分析歌曲的前后关系、固定位置、各年度趋势。",
    compareTitle: "歌单对比",
    compareDesc: "对比两场演出的歌单，将共同曲目、差异、曲序变化可视化。",
    tourHeatmapTitle: "巡演热力图",
    tourHeatmapDesc: "横跨一次巡演的所有场次，一览各歌曲是否演出与曲序。",
    similarityTitle: "歌单相似度排行",
    similarityDesc: "不同巡演之间歌单特别相似的场次组合排行。",
    postsTitle: "相关帖子",
    postsDesc: "按时间顺序横向显示歌曲、演唱会、各场次的相关帖子。",
    quizTitle: "Reol 粉丝类型测试",
    quizDesc: "回答 20 道题，测出你的粉丝类型！",
    guideTitle: "演唱会参加指南",
    guideDesc: "从携带物品、穿着、礼仪到远征的诀窍",
  },
  explore: {
    kicker: "EXPLORE — 主要内容",
    discography: "作品与全部歌曲信息",
    live: "演出信息、歌单",
    place: "取景地地图",
    photo: "相册",
  },
  otd: {
    yesterday: "昨天",
    dayBeforeYesterday: "前天",
    daysAgo: (n: number) => `${n} 天前`,
    tomorrow: "明天",
    dayAfterTomorrow: "后天",
    daysLater: (n: number) => `${n} 天后`,
    releaseSuffix: " 发行",
    releaseTitle: (title: string) => `《${title}》`,
    kickerMonth: "ON THIS MONTH — 本月纪事",
    kickerDay: "ON THIS DAY — 历史上的今天",
    yearsAgoToday: (years: number) => `${years} 年前的今天`,
    anniversaryIn: (when: string, years: number) => `${when}满 ${years} 周年`,
    anniversaryWas: (when: string, years: number) => `${when}是 ${years} 周年`,
    monthEntry: (md: string, years: number) => `${md}(${years} 年前)`,
    upcoming: (days: number, years: number) => `再过 ${days} 天满 ${years} 周年`,
    seeAll: "查看这一天的全部记录 →",
  },
  common: {
    postTiming: (kind: "live" | "release", rel: RelativeDate): string => {
      const ref = kind === "live" ? "演出" : "发行";
      if (rel.unit === "same") return `${ref}当天`;
      const unit = rel.unit === "day" ? "天" : rel.unit === "month" ? "个月" : "年";
      return `${ref}${rel.direction === "before" ? "前" : "后"} ${rel.amount} ${unit}`;
    },
    postCategory: { 本人: "本人", 公式: "官方", メディア: "媒体" } as Record<string, string>,
    showMore: (n: number) => `显示更多(${n} 条)`,
    backToTop: "返回页面顶部",
    backHome: "返回 HOME",
    loading: "加载中...",
    loadError: (err: string) => `加载失败：${err}`,
  },
  discography: {
    intro: "DISCOGRAPHY 按时间轴收录至今的作品信息与翻唱视频等。",
    metaDescription: "按时间轴收录 Reol 至今的作品信息与翻唱视频等，也能查看各歌曲的链接。",
    collectionLabel: "收藏(已拥有/已观看)",
    collectionHint: "点击各卡片的「✓」即可记录已拥有/已观看(仅保存在此设备的浏览器中)",
    count: (n: number) => `${n} 条`,
    unknownYear: "未知",
    helpFilter: "点击上方的话题标签即可简单筛选列表",
    helpFilterExample: "例：点击「#れをる」只会显示「れをる」名义的信息",
    helpPlayBefore: "点击标题左侧的",
    helpPlayAfter: "即可在页面上方的播放器播放视频",
    helpOpenBefore: "点击「",
    helpOpenAfter: "展开」即可显示收录曲目与各歌曲的链接",
    helpCommentBefore: "歌名右侧若显示「",
    helpCommentAfter: "」，点击即可查看 Spotify 链接与歌曲分析信息",
    detail: "详情",
    detailTitle: "查看歌曲页面(演出统计、MV、相关帖子)",
    markOwned: "标记为已拥有/已观看",
    unmarkOwned: "从已拥有中移除",
    owned: "已拥有",
    ownedCount: (n: number) => `${n} 人已拥有/已观看`,
    tracks: "收录曲目",
    copied: "已复制",
    copyTitles: "复制曲目",
    interview: "访谈",
    relatedPosts: "相关帖子",
  },
  formats: {
    SG: "单曲",
    配信SG: "数字单曲",
    EP: "EP",
    miniAL: "迷你专辑",
    fullAL: "专辑",
    "DVD/BD": "DVD/BD",
    MV: "MV",
    歌ってみた: "翻唱",
    LP: "黑胶",
  },
  live: {
    metaDescription: "Reol 至今参与的个人演唱会、巡演、音乐节等信息，收录各场演出的歌单、报道与相关帖子。",
    intro1: "LIVE 收录 Reol 至今参与的个人演唱会、巡演、音乐节等信息。",
    intro2: "收录各场演出的歌单、相关帖子与演出报道，即使没能参加的场次，也能感受到当时的氛围。",
    attendanceLabel: "参加记录",
    attendanceHint: "点击各演出卡片的「✓」即可记录已参加(仅保存在此设备的浏览器中)",
    heardLabel: "现场听过的歌曲",
    heardHint: "根据已标记参加的演出歌单统计(以曾在现场演出过的歌曲为对象)",
    heardList: (n: number) => `听过的歌曲 (${n})`,
    unheardList: (n: number) => `还没在现场听过的歌曲 (${n}，按演出次数排序)`,
    ongoing: "进行中",
    upcoming: "近期预定",
    helpFilter: "点击上方的话题标签即可简单筛选列表",
    helpFilterExample: "例：点击「#れをる」只会显示「れをる」名义的信息",
    helpDetailBefore: "点击「",
    helpDetailAfter: " 详情」即可显示演出报道、举办地点、歌单、相关帖子等信息",
    tagEvent: "活动演出",
    tagOneman: "个人演唱会",
    count: (n: number) => `${n} 条`,
    unknownYear: "未知",
    setlistCopyTitle: (title: string) => `${title} 歌单`,
    markAttended: "标记为已参加",
    unmarkAttended: "从已参加中移除",
    attended: "已参加",
    attendedInYear: (n: number) => `已参加 ${n} 场`,
    showCount: (n: number) => `${n} 场公演`,
    songCount: (n: number) => `${n} 首`,
    detailTitle: "查看这场演出的页面(歌单、会场、相关帖子)",
    detail: "详情",
    youtubePlaylist: "YouTube 播放列表",
    attendedCount: (n: number) => `${n} 人已参加`,
    setlist: "歌单",
    copied: "已复制",
    copy: "复制",
    songPageTitle: "查看歌曲页面",
    songStatsTitle: "在歌曲统计中查看演出记录",
    noSetlist: "目前尚未登记歌单信息",
    toShowPage: "查看演出页面",
    spoilerBadgeTitle: "巡演进行中，歌单含有剧透",
    spoilerBadge: "⚠ 剧透注意",
    expand: "展开",
    spoilerNoticeTitle: "⚠ 剧透注意",
    spoilerNotice1: "本次巡演目前正在进行中。",
    spoilerNotice2: "歌单与相关帖子含有剧透内容。",
    spoilerNotice3: "请确认后再浏览。",
    showSpoiler: "了解剧透风险并显示",
    relatedPosts: "相关帖子",
  },
  song: {
    shareText: (song: string) => `Reol〈${song}〉的演出统计、MV、流媒体链接 | !Legit(非官方粉丝网站)`,
    totalPlays: "累计演出",
    playsValue: (n: string) => `${n} 次`,
    firstPlayed: "首次演出",
    lastPlayed: "最近演出",
    album: "收录作品：",
    lyrics: "作词",
    music: "作曲",
    stream: "在流媒体收听",
    lyricsLink: "查看歌词(歌ネット，日文)",
    watchMv: "观看官方 MV",
    articles: "关于这首歌的文章(日文)",
    shareTitle: "在 X 分享这首歌的页面",
    share: "在 X 分享",
    historyKicker: (n: string) => `PERFORMANCE HISTORY — ${n} 条`,
    noHistoryTitle: "目前还没有现场演出记录",
    noHistoryDesc: "今后在演唱会上演出时，演出记录会添加到这里。",
    venueTbd: "会场待定",
    closeVideo: "关闭",
    watchVideo: "观看视频",
    relatedPosts: "相关帖子",
    toStatsTitle: "前往歌曲统计",
    toStatsDesc: "一次查看全部歌曲的演出次数、首次演出、最近演出。",
    toSearchTitle: "前往横向搜索",
    toSearchDesc: "横跨歌曲、演唱会、取景地的关键词搜索。",
    backToTop: "返回首页",
    metaLead: (song: string) => `Reol〈${song}〉的现场演出统计。`,
    metaPlays: (n: number, first: string | null) => `累计演出 ${n} 次，首次演出为 ${first ?? "未知"}。`,
    metaNoPlays: "尚未确认到现场演出记录。",
    metaTail: "收录演出记录、官方 MV、流媒体链接。",
    breadcrumbStats: "歌曲统计",
  },
  show: {
    setlistCopyTitle: (title: string) => `${title} 歌单`,
    shareText: (heading: string) => `Reol「${heading}」的歌单、会场信息 | !Legit(非官方粉丝网站)`,
    venue: "会场",
    venueSite: "查看会场网站",
    reliveTitle: "Relive Player (β) — 用本地音源播放这份歌单",
    relive: "Relive Player (β，需自备音源)",
    shareTitle: "在 X 分享这场演出的页面",
    share: "在 X 分享",
    mapTitle: (heading: string) => `会场地图 - ${heading}`,
    setlistKicker: (n: number) => `SETLIST — ${n} 首`,
    mcCount: (n: number) => `(MC ${n})`,
    copied: "已复制",
    copy: "复制",
    noSetlistTitle: "目前尚未登记歌单信息",
    noSetlistDesc: "确认信息后将会补充。",
    closeVideo: "关闭",
    watchVideo: "观看视频",
    venueTbd: "会场待定",
    relatedPosts: "相关帖子",
    toArchiveTitle: "前往 LIVE 资料库",
    toArchiveDesc: "一次查看历年演唱会的歌单、演出信息。",
    toSearchTitle: "前往横向搜索",
    toSearchDesc: "横跨歌曲、演唱会、取景地的关键词搜索。",
    backToTop: "返回首页",
    metaLead: (name: string) => `Reol「${name}」的演出信息。`,
    metaVenue: (place: string) => `会场：${place}。`,
    metaSetlist: (n: number) => `收录 ${n} 首歌单。`,
  },
  stats: {
    title: "歌曲统计",
    metaDescription: "Reol 全部歌曲的现场演出统计，可一览累计演出次数、首次演出日、最近演出日。",
    lead: "一览演唱会上演出过的歌曲的累计演出次数、首次/最近演出日。",
    rulesLink: "关于统计规则 →",
    totalPlays: "演出次数(累计)",
    uniqueSongs: "演出过的歌曲",
    searchPlaceholder: "🔍 按歌名搜索",
    sortPlays: "演出次数(多)",
    sortLast: "最近演出(新)",
    sortFirst: "首次演出(旧)",
    sortName: "歌名",
    songCount: (n: string) => `${n} 首`,
    toSongPageTitle: "前往歌曲页面",
    firstPlayed: "首次演出",
    lastPlayed: "最近",
    toAlbum: "查看收录作品 →",
    toSongPage: "前往这首歌的页面 →",
    watchMv: "▶ 观看官方 MV",
    stream: "♪ 在流媒体收听",
    history: "演出记录",
    historyCount: (n: number) => `(${n} 条)`,
    noMatch: "找不到符合的歌曲",
    backToTop: "← 返回首页",
    historyLoadError: "无法加载演出记录",
    historyLoading: "正在加载演出记录...",
    historyEmpty: "没有演出记录",
  },
};

export default zhHans;
